import React, { useMemo, useRef } from 'react';
import * as S from './DataFeed.style';
import { useStore } from '../../store/useStore';
import { transformForDisplay } from '../../services/transformer';
import { Reachability } from '../Charts/Reachability/Reachability.component';
import { Field } from '../Charts/ForceGraph/ForceGraph.components';
import { useResizeObserver } from '../../hooks/useResizeObserver';

const JSON_TOKEN_REGEX =
  /("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"\s*:?)|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|(?:\{|\}|\[|\]|,)/g;

const highlightJsonLine = (line: string, lineIndex: number) => {
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = JSON_TOKEN_REGEX.exec(line)) !== null) {
    const token = match[0];
    const start = match.index;

    if (start > cursor) {
      parts.push(
        <span key={`txt-${lineIndex}-${cursor}`}>{line.slice(cursor, start)}</span>,
      );
    }

    let className = 'token-punctuation';
    if (/^"/.test(token) && /:$/.test(token)) {
      className = 'token-key';
    } else if (/^"/.test(token)) {
      className = 'token-string';
    } else if (/^(true|false|null)$/.test(token)) {
      className = 'token-literal';
    } else if (/^-?\d/.test(token)) {
      className = 'token-number';
    }

    parts.push(
      <span className={className} key={`tok-${lineIndex}-${start}`}>
        {token}
      </span>,
    );
    cursor = start + token.length;
  }

  if (cursor < line.length) {
    parts.push(
      <span key={`tail-${lineIndex}-${cursor}`}>{line.slice(cursor)}</span>,
    );
  }

  return parts;
};

interface DataFeedProps {
  rawOnly?: boolean;
}

export const DataFeed = ({ rawOnly = false }: DataFeedProps) => {
  const dataFeed = useStore((state) => state.dataFeed);
  const reachabilityRef = useRef<HTMLDivElement>(null);
  const reachabilitySize = useResizeObserver(reachabilityRef);

  const displayPayload = useMemo(() => {
    return transformForDisplay(dataFeed);
  }, [dataFeed]);

  const rawFeed = useMemo(() => {
    return displayPayload ? JSON.stringify(displayPayload, undefined, 2) : 'awaiting data...';
  }, [displayPayload]);

  const highlightedRawFeed = useMemo(() => {
    if (!displayPayload) return rawFeed;

    return rawFeed.split('\n').map((line, lineIndex, allLines) => (
      <React.Fragment key={`line-${lineIndex}`}>
        {highlightJsonLine(line, lineIndex)}
        {lineIndex < allLines.length - 1 ? '\n' : null}
      </React.Fragment>
    ));
  }, [displayPayload, rawFeed]);

  if (rawOnly) {
    return (
      <S.RawFeedPanel>
        <S.RawFeedHeader>
          <S.RawFeedLabel>raw feed</S.RawFeedLabel>
          <S.RawFeedState>{displayPayload ? 'live json stream' : 'standby'}</S.RawFeedState>
        </S.RawFeedHeader>
        <S.DataFeedPrettify>{highlightedRawFeed}</S.DataFeedPrettify>
      </S.RawFeedPanel>
    );
  }

  return (
    <>
      <S.DataFeedWrapper>
        <Field />
        <S.ReachabilityCaption>1H Move Distribution</S.ReachabilityCaption>
        <S.ReachabilitySlot ref={reachabilityRef}>
          <Reachability size={reachabilitySize} />
        </S.ReachabilitySlot>
        <S.RawFeedPanel>
          <S.RawFeedHeader>
            <S.RawFeedLabel>raw feed</S.RawFeedLabel>
            <S.RawFeedState>{displayPayload ? 'live json stream' : 'standby'}</S.RawFeedState>
          </S.RawFeedHeader>
          <S.DataFeedPrettify>{highlightedRawFeed}</S.DataFeedPrettify>
        </S.RawFeedPanel>
      </S.DataFeedWrapper>
    </>
  );
};
